import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import type { CustomFieldsSchema } from "@forgepoint/shared";

export interface OpportunitySettings {
  id: string;
  organizationId: string;
  defaultProbability: number;
  customFieldsSchema: CustomFieldsSchema | null;
  autoCloseAfterDays: number | null;
  autoCloseTargetStageId: string | null;
  opportunityNamingGuidance: string | null;
  createdAt: Date;
  updatedAt: Date;
}

interface RawOpportunitySettings {
  id: string;
  organizationId: string;
  defaultProbability: number;
  customFieldsSchema: CustomFieldsSchema | null;
  autoCloseAfterDays: number | null;
  autoCloseTargetStageId: string | null;
  opportunityNamingGuidance: string | null;
  createdAt: string;
  updatedAt: string;
}

interface OpportunitySettingsState {
  settings: OpportunitySettings | null;
  isLoading: boolean;
  error: string | null;
  fetchSettings: () => Promise<void>;
  updateSettings: (params: {
    defaultProbability?: number;
    customFieldsSchema?: CustomFieldsSchema | null;
    autoCloseAfterDays?: number | null;
    autoCloseTargetStageId?: string | null;
    opportunityNamingGuidance?: string | null;
  }) => Promise<boolean>;
}

const toSettings = (raw: RawOpportunitySettings): OpportunitySettings => ({
  ...raw,
  createdAt: new Date(raw.createdAt),
  updatedAt: new Date(raw.updatedAt),
});

export const useOpportunitySettingsStore = create<OpportunitySettingsState>()(
  immer((set) => ({
    settings: null,
    isLoading: false,
    error: null,

    fetchSettings: async () => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetchApi("opportunity-settings", "GET");
        if (!response.ok) {
          throw new Error("Failed to fetch opportunity settings");
        }
        const raw = (
          response.data as { settings: RawOpportunitySettings | null }
        ).settings;
        set({
          settings: raw ? toSettings(raw) : null,
          isLoading: false,
        });
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "An unknown error occurred";
        captureException(err as Error);
        set({ error: errorMsg, isLoading: false });
      }
    },

    updateSettings: async (params) => {
      set({ isLoading: true, error: null });
      try {
        const response = await fetchApi("opportunity-settings", "POST", {
          body: params,
        });
        if (!response.ok) {
          throw new Error(
            response.error || "Failed to update opportunity settings",
          );
        }
        const raw = (response.data as { settings: RawOpportunitySettings })
          .settings;
        set({ settings: toSettings(raw), isLoading: false });
        return true;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : "An unknown error occurred";
        captureException(err as Error);
        set({ error: errorMsg, isLoading: false });
        return false;
      }
    },
  })),
);
